import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageSkeleton } from "@/components/PageSkeleton";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { CalendarClock, CalendarOff, Trash2, Plus, MapPin } from "lucide-react";

const monthStr = () => new Date().toISOString().slice(0, 7);
const monthRange = (m) => {
  const [y, mo] = m.split("-").map(Number);
  const last = new Date(y, mo, 0).getDate();
  return { start: `${m}-01`, end: `${m}-${String(last).padStart(2, "0")}` };
};

export default function AttendanceSchedule() {
  const [users, setUsers] = useState(null);
  const [month, setMonth] = useState(monthStr());
  const [shifts, setShifts] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [sForm, setSForm] = useState({ user_id: "", date: "", start: "08:00", end: "17:00", note: "" });
  const [lForm, setLForm] = useState({ user_id: "", date: "", end_date: "", note: "" });
  const [homes, setHomes] = useState([]);
  const [hForm, setHForm] = useState({ user_id: "", lat: "", lng: "", label: "Rumah", radius_m: 100 });

  const load = () => {
    const { start, end } = monthRange(month);
    api.get(`/attendance/shifts?start=${start}&end=${end}`).then((r) => setShifts(r.data.rows));
    api.get(`/attendance/leaves?start=${start}&end=${end}`).then((r) => setLeaves(r.data.rows));
    api.get("/attendance/home-locations").then((r) => setHomes(r.data.rows));
  };
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => ["admin", "karyawan"].includes(u.role))));
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const saveShift = async () => {
    if (!sForm.user_id || !sForm.date) { toast.error("Pilih pengguna & tanggal"); return; }
    try {
      await api.post("/attendance/shifts", sForm);
      toast.success("Shift tersimpan");
      setSForm({ ...sForm, date: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delShift = async (id) => { try { await api.delete(`/attendance/shifts/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const saveLeave = async () => {
    if (!lForm.user_id || !lForm.date) { toast.error("Pilih pengguna & tanggal mulai"); return; }
    if (lForm.end_date && lForm.end_date < lForm.date) { toast.error("Tanggal akhir libur tidak boleh sebelum tanggal mulai"); return; }
    try {
      const r = await api.post("/attendance/leaves", { ...lForm, end_date: lForm.end_date || null });
      if (r.data.warning) toast.warning(r.data.warning); else toast.success(`Libur tersimpan${r.data.days > 1 ? ` (${r.data.days} hari)` : ""}`);
      setLForm({ ...lForm, date: "", end_date: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delLeave = async (id) => { try { await api.delete(`/attendance/leaves/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const grabHomeGps = () => {
    if (!navigator.geolocation) { toast.error("GPS tidak tersedia di perangkat ini"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setHForm({ ...hForm, lat: p.coords.latitude, lng: p.coords.longitude }); toast.success("Titik lokasi terisi dari GPS"); },
      () => toast.error("Lokasi tidak terbaca — aktifkan izin lokasi"),
      { enableHighAccuracy: true, timeout: 10000 });
  };
  const saveHome = async () => {
    if (!hForm.user_id || hForm.lat === "" || hForm.lng === "") { toast.error("Pilih admin & isi lokasi"); return; }
    try {
      await api.post("/attendance/home-locations", { ...hForm, lat: parseFloat(hForm.lat), lng: parseFloat(hForm.lng), radius_m: parseInt(hForm.radius_m) || 100 });
      toast.success("Lokasi rumah admin tersimpan");
      setHForm({ ...hForm, lat: "", lng: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delHome = async (id) => { try { await api.delete(`/attendance/home-locations/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (!users) return <PageSkeleton stats={0} rows={6} />;

  return (
    <div className="space-y-4" data-testid="attendance-schedule-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight flex items-center gap-2"><CalendarClock className="h-5 w-5 text-[#C9A227]" /> Jadwal & Libur</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Atur shift Admin (dukung lintas tengah malam) & tandai hari libur.</p>
        </div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="sched-month" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-4 space-y-3">
          <h3 className="font-heading text-sm font-semibold">Tambah Shift Admin</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Pengguna</Label>
              <Select value={sForm.user_id} onValueChange={(v) => setSForm({ ...sForm, user_id: v })}>
                <SelectTrigger data-testid="shift-user"><SelectValue placeholder="Pilih admin/karyawan" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u._id} value={u._id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Tanggal</Label><Input type="date" value={sForm.date} onChange={(e) => setSForm({ ...sForm, date: e.target.value })} data-testid="shift-date" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Catatan</Label><Input value={sForm.note} onChange={(e) => setSForm({ ...sForm, note: e.target.value })} placeholder="mis. Shift Malam" data-testid="shift-note" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Jam Mulai</Label><Input type="time" value={sForm.start} onChange={(e) => setSForm({ ...sForm, start: e.target.value })} data-testid="shift-start" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Jam Selesai</Label><Input type="time" value={sForm.end} onChange={(e) => setSForm({ ...sForm, end: e.target.value })} data-testid="shift-end" /></div>
          </div>
          <p className="text-[10px] text-muted-foreground">Jika jam selesai ≤ jam mulai, dianggap pulang hari berikutnya (lintas tengah malam).</p>
          <Button variant="gold" className="w-full gap-1.5" onClick={saveShift} data-testid="shift-save"><Plus className="h-4 w-4" /> Simpan Shift</Button>
          <div className="space-y-1.5 max-h-72 overflow-auto" data-testid="shift-list">
            {shifts.length === 0 && <p className="text-xs text-muted-foreground py-2">Belum ada shift bulan ini.</p>}
            {shifts.map((s) => (
              <div key={s._id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`shift-row-${s._id}`}>
                <div className="min-w-0"><p className="font-medium truncate">{s.user_name} · {formatDate(s.date)}</p><p className="text-[11px] text-muted-foreground">{s.start}–{s.end}{Number(s.end.replace(":", "")) <= Number(s.start.replace(":", "")) ? " (+1 hari)" : ""}{s.note ? ` · ${s.note}` : ""}</p></div>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => delShift(s._id)} data-testid={`shift-del-${s._id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-4 space-y-3">
          <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><CalendarOff className="h-4 w-4 text-amber-500" /> Tandai Hari Libur</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Pengguna</Label>
              <Select value={lForm.user_id} onValueChange={(v) => setLForm({ ...lForm, user_id: v })}>
                <SelectTrigger data-testid="leave-user"><SelectValue placeholder="Pilih admin/karyawan" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u._id} value={u._id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Tanggal Mulai</Label><Input type="date" value={lForm.date} onChange={(e) => setLForm({ ...lForm, date: e.target.value })} data-testid="leave-date" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Tanggal Akhir</Label><Input type="date" value={lForm.end_date} onChange={(e) => setLForm({ ...lForm, end_date: e.target.value })} data-testid="leave-end-date" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Catatan</Label><Input value={lForm.note} onChange={(e) => setLForm({ ...lForm, note: e.target.value })} data-testid="leave-note" /></div>
          </div>
          <p className="text-[10px] text-muted-foreground">Jatah libur Admin 4 hari/bulan — sistem memperingatkan bila melebihi, tetapi tetap disimpan.</p>
          <Button variant="gold" className="w-full gap-1.5" onClick={saveLeave} data-testid="leave-save"><Plus className="h-4 w-4" /> Simpan Libur</Button>
          <div className="space-y-1.5 max-h-72 overflow-auto" data-testid="leave-list">
            {leaves.length === 0 && <p className="text-xs text-muted-foreground py-2">Belum ada libur bulan ini.</p>}
            {leaves.map((l) => (
              <div key={l._id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`leave-row-${l._id}`}>
                <div className="min-w-0"><p className="font-medium truncate">{l.user_name} · {formatDate(l.date)}{l.end_date && l.end_date !== l.date ? ` → ${formatDate(l.end_date)} (${l.days || ""} hari)` : ""}</p>{l.note && <p className="text-[11px] text-muted-foreground">{l.note}</p>}</div>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => delLeave(l._id)} data-testid={`leave-del-${l._id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-4 space-y-3">
        <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><MapPin className="h-4 w-4 text-rose-500" /> Lokasi Rumah Admin (Privat)</h3>
        <p className="text-[11px] text-muted-foreground">Titik rumah tiap admin untuk validasi absen di rumah saat GPS aktif. Hanya Owner yang mengelola data ini.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs">Admin</Label>
            <Select value={hForm.user_id} onValueChange={(v) => setHForm({ ...hForm, user_id: v })}>
              <SelectTrigger data-testid="home-user"><SelectValue placeholder="Pilih admin" /></SelectTrigger>
              <SelectContent>{users.filter((u) => u.role === "admin").map((u) => <SelectItem key={u._id} value={u._id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Latitude</Label><Input type="number" step="any" value={hForm.lat} onChange={(e) => setHForm({ ...hForm, lat: e.target.value })} data-testid="home-lat" placeholder="-6.2" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Longitude</Label><Input type="number" step="any" value={hForm.lng} onChange={(e) => setHForm({ ...hForm, lng: e.target.value })} data-testid="home-lng" placeholder="106.8" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Radius (meter)</Label><Input type="number" value={hForm.radius_m} onChange={(e) => setHForm({ ...hForm, radius_m: e.target.value })} data-testid="home-radius" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Label</Label><Input value={hForm.label} onChange={(e) => setHForm({ ...hForm, label: e.target.value })} data-testid="home-label" /></div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-1.5" onClick={grabHomeGps} data-testid="home-gps-btn"><MapPin className="h-3.5 w-3.5" /> Pakai Lokasi Perangkat Ini</Button>
          <Button variant="gold" className="flex-1 gap-1.5" onClick={saveHome} data-testid="home-save"><Plus className="h-4 w-4" /> Simpan Lokasi Rumah</Button>
        </div>
        <div className="space-y-1.5" data-testid="home-list">
          {homes.length === 0 && <p className="text-xs text-muted-foreground py-2">Belum ada lokasi rumah terdaftar.</p>}
          {homes.map((h) => (
            <div key={h._id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`home-row-${h._id}`}>
              <div className="min-w-0"><p className="font-medium truncate">{h.user_name} · {h.label}</p><p className="text-[11px] text-muted-foreground">{h.lat}, {h.lng} · radius {h.radius_m} m</p></div>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => delHome(h._id)} data-testid={`home-del-${h._id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
